import React from 'react';
import { Box, Typography, Slider, Badge, Accordion } from '@lucid/lego';
import styles from './Details.module.scss';

interface TimelineItem {
  status: string;
  date: string;
  completed: boolean;
}

interface PartAndLabor {
  item: string;
  cost: string;
}

export interface ClaimDetail {
  id: string;
  claimId: string;
  vin: string;
  vehicleModel: string;
  lucidId: string;
  claimType: string;
  submissionDate: string;
  status: string;
  amount: string;
  reviewer: string;
  reviewerTitle: string;
  submittedDate: string;
  lastUpdatedDate: string;
  description: string;
  partsAndLabor: PartAndLabor[];
  totalAmount: string;
  timeline: TimelineItem[];
  reviewerNotes: string;
}

interface DetailsProps {
  theme: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
  claimDetail: ClaimDetail | null;
}

const Details: React.FC<DetailsProps> = ({ theme, isOpen, onClose, claimDetail }) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const getStatusBadgeColor = (status: string): 'error' | 'warning' | 'informative' | 'success' => {
    switch (status) {
      case 'under-review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'info-required':
        return 'error';
      case 'draft':
        return 'informative';
      default:
        return 'informative';
    }
  };

  const formatStatus = (status: string): string => {
    return status
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const title = claimDetail ? (
    <Box className={styles.sliderTitle}>
      <Typography variant="headline" size="small" color="primary-1" theme={invertedTheme}>
        Claim Details - {claimDetail.claimId}
      </Typography>
      <Badge
        label={formatStatus(claimDetail.status)}
        color={getStatusBadgeColor(claimDetail.status)}
        type="secondary"
        theme={invertedTheme}
      />
    </Box>
  ) : (
    'Claim Details'
  );

  return (
    <Slider
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      direction="right"
      width="1000px"
      theme={invertedTheme}
    >
      {claimDetail ? (
        <Box className={styles.sliderContent}>
          {/* Vehicle & Claim Overview */}
          <Accordion
            title="Claim Overview"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.fieldGrid}>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  VIN
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.vin}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Lucid ID
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.lucidId}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Vehicle Model
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.vehicleModel}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Claim Type
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.claimType} Claim
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Submitted Date
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.submittedDate}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Last Updated
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.lastUpdatedDate}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Reviewer
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.reviewer}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Reviewer Title
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.reviewerTitle}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Total Amount
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.totalAmount}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Accordion>

          {/* Claim Information */}
          <Accordion
            title="Claim Information"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.accordionContent}>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Description
                </Typography>
                <Box className={styles.fieldValue}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {claimDetail.description}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Parts & Labor
                </Typography>
                <Box className={styles.costBreakdown}>
                  {claimDetail.partsAndLabor.map((item, index) => (
                    <Box key={index} className={styles.costItem}>
                      <Typography variant="body" size="small" theme={invertedTheme} color="white">
                        {item.item}
                      </Typography>
                      <Typography variant="body" size="small" theme={invertedTheme} color="white">
                        {item.cost}
                      </Typography>
                    </Box>
                  ))}
                  <Box className={styles.costTotal}>
                    <Typography variant="body" size="medium" theme={invertedTheme} color="white">
                      Total
                    </Typography>
                    <Typography variant="body" size="medium" theme={invertedTheme} color="white">
                      {claimDetail.totalAmount}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Accordion>

          {/* Claim Timeline */}
          <Accordion
            title="Claim Timeline"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.accordionContent}>
              {claimDetail.timeline.map((item, index) => (
                <Box key={index} className={styles.timelineStep}>
                  <Box
                    className={
                      item.completed ? styles.timelineDotActive : styles.timelineDotPending
                    }
                  />
                  <Box className={styles.timelineContent}>
                    <Typography
                      variant="body"
                      size="small"
                      theme={invertedTheme}
                      color={item.completed ? 'primary-1' : 'gray-5'}
                    >
                      {item.status}
                    </Typography>
                    {item.date && (
                      <Box className={styles.fieldValue}>
                        <Typography variant="body" size="small" theme={invertedTheme} color="white">
                          {item.date}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Accordion>

          {/* Reviewer Notes */}
          <Accordion
            title="Reviewer Notes"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box>
              <Typography variant="body" size="small" theme={invertedTheme} color="white">
                {claimDetail.reviewerNotes}
              </Typography>
            </Box>
          </Accordion>
        </Box>
      ) : null}
    </Slider>
  );
};

export default Details;
